import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * Combined listings morph <main> (including this block). Do not re-touch the DOM in that
 * path — a second sync can wipe content already moved into the live tree.
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    // Combined listing product switch: <main> was already morphed with the new product's
    // price markup. Only sync the product id — do not mutate children or re-fire installments.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
      return;
    }

    if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(/** @type {Element} */ (newPrice.cloneNode(true)));
    }

    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      this.querySelector('[ref="priceContainer"]')?.insertAdjacentElement(
        'afterend',
        /** @type {Element} */ (newNote.cloneNode(true))
      );
    } else {
      volumePricingNote.replaceWith(/** @type {Element} */ (newNote.cloneNode(true)));
    }

    const newPricePerSqm = newProductPrice.querySelector('.price-per-sqm');
    const pricePerSqm = this.querySelector('.price-per-sqm');

    if (!newPricePerSqm) {
      pricePerSqm?.remove();
    } else if (!pricePerSqm) {
      this.appendChild(/** @type {Element} */ (newPricePerSqm.cloneNode(true)));
    } else {
      pricePerSqm.replaceWith(/** @type {Element} */ (newPricePerSqm.cloneNode(true)));
    }

    const newPerBox = newProductPrice.querySelector('.per-box-size');
    const perBox = this.querySelector('.per-box-size');

    if (!newPerBox) {
      perBox?.remove();
    } else if (!perBox) {
      this.appendChild(/** @type {Element} */ (newPerBox.cloneNode(true)));
    } else {
      perBox.replaceWith(/** @type {Element} */ (newPerBox.cloneNode(true)));
    }

    this.#updateInstallments(event);
  };

  /**
   * Updates installments (SPI banner) variant ID to trigger payment terms re-render.
   * @param {VariantUpdateEvent} event
   */
  #updateInstallments(event) {
    const input_selector = `#product-form-installment-${this.dataset.blockId} input[name="id"]`;
    const installmentsInput = /** @type {HTMLInputElement|null} */ (this.querySelector(input_selector));
    if (installmentsInput) {
      installmentsInput.value = event.detail.resource?.id ?? '';
      installmentsInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
